import React, { useEffect, useState } from "react";
import { getWithTokenAPI, postWithTokenAPI, putAPI } from "../../utils/Api";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  Pagination,
} from "@mui/material";
import CategoryIcon from "@mui/icons-material/Category";

function CategoryManagement() {
  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";

  const [categories, setCategories] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [addCategoryErrorMsg, setAddCategoryErrorMsg] = useState("");
  const [filteredCategories, setFilteredCategories] = useState([]);
  const permissions = JSON.stringify(localStorage.getItem("permissions"));

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesPerPage] = useState(10); 

  const [newCategory, setNewCategory] = useState({
    category_name: "",
    image: "",
    description: "",
    status: "Active",
    Approval_status: "Pending",
  });

  const fetchCategoryList = async (filterType = null, filterValue = null) => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      let url = "/category/find-all";
      const queryParams = [];

      if (filterType && filterValue) {
        queryParams.push(`${filterType}=${filterValue}`);
      }

      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }
      console.log(url, "checkingg url");

      const res = await getWithTokenAPI(token, url);
      if (res.statusCode === 200) {
        setCategories(res.data.categories || []);
      } else {
        toast.error(res.message || "Failed to load categories.");
        setCategories([]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load categories due to a network error.");
      setCategories([]); 
    } finally {
      setLoading(false); 
    }
  };

  // Pagination logic
  const indexOfLastOrder = currentPage * categoriesPerPage;
  const indexOfFirstOrder = indexOfLastOrder - categoriesPerPage;
  const currentCategories = categories.slice(
    indexOfFirstOrder,
    indexOfLastOrder
  );
  const totalPages = Math.ceil(categories.length / categoriesPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleTabChange = async (tab) => {
    setActiveTab(tab);
    setLoading(true);
    try {
      let categoryData = [];
      switch (tab) {
        case "all":
          categoryData = await fetchCategoryList();
          break;
        case "active":
          categoryData = await fetchCategoryList("status", "Active");
          break;
        case "inactive":
          categoryData = await fetchCategoryList("status", "Inactive");
          break;
        case "Pending":
          categoryData = await fetchCategoryList("Approval_status", "Pending");
          break;
        case "Approval":
          categoryData = await fetchCategoryList("Approval_status", "Approval");
          break;
        case "Reject":
          categoryData = await fetchCategoryList("Approval_status", "Reject");
          break;
        default:
          categoryData = await fetchCategoryList("status", "Active");
      }
    } catch (error) {
      console.error("Tab change error:", error);
      toast.error("Failed to load categories for this tab.");
      setLoading(false);
    }
  };

  useEffect(() => {
    handleTabChange("active"); 
  }, []);

  // Edit modal open handler
  const handleEditClick = (category) => {
    setSelectedCategory(category);
    setApprovalStatus(category.Approval_status || "Pending");
    setShowEditModal(true);
  };

  // Update category status (Active/Inactive)
  const updateCategoryStatus = async (categoryId, currentStatus) => {
    const token = localStorage.getItem("token");
    const newStatus = currentStatus === "Active" ? "Inactive" : "Active";

    // Optimistic UI update: update the UI immediately
    setCategories((prev) =>
      prev.map((c) => (c._id === categoryId ? { ...c, status: newStatus } : c))
    );
    setLoading(true); // Start loading state for the API call

    try {
      const res = await putAPI(token, `/category/update`, {
        _id: categoryId,
        status: newStatus,
      });

      if (res.statusCode !== 200) {
        throw new Error(res.message || "Failed to update status");
      }

      toast.success(res.message || "Category updated successfully", {
        autoClose: 1000,
        onClose: () => {
          // Refresh categories after successful update to ensure data consistency
          handleTabChange(activeTab);
        },
      });
    } catch (error) {
      console.error("Update category status error:", error);
      toast.error(error.message || "Failed to update status");

      // Revert UI changes on error to reflect actual state
      setCategories((prev) =>
        prev.map((c) =>
          c._id === categoryId ? { ...c, status: currentStatus } : c
        )
      );
    } finally {
      setLoading(false); // Stop loading regardless of success or failure
    }
  };

  const updateApprovalStatus = async () => {
    const token = localStorage.getItem("token");
    setLoading(true); // Start loading

    const payload = {
      _id: selectedCategory._id,
      category_name: selectedCategory.category_name,
      description: selectedCategory.description,
      image: selectedCategory.image,
      status: selectedCategory.status,
      Approval_status: approvalStatus,
    };

    try {
      const res = await putAPI(token, "/category/update", payload);

      if (res.statusCode !== 200) {
        throw new Error(res.message || "Failed to update category");
      }

      toast.success(res.message || "Category updated successfully", {
        autoClose: 1000,
        onClose: () => {
          setShowEditModal(false); // Close modal on success
          handleTabChange(activeTab); // Refresh categories list
        },
      });
    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.message || "Failed to update");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setAddCategoryErrorMsg(""); // Clear previous error messages

    // --- Input Validations ---
    if (!newCategory.category_name.trim()) {
      setAddCategoryErrorMsg("Category name is required.");
      return;
    }
    // Image URL validation: if image is provided, it must be a valid URL
    if (
      newCategory.image.trim() &&
      !newCategory.image.trim().match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i)
    ) {
      setAddCategoryErrorMsg(
        "Please enter a valid Image URL or leave it empty."
      );
      return;
    }
    // Description is required
    if (!newCategory.description.trim()) {
      setAddCategoryErrorMsg("Description is required.");
      return;
    }

    // --- API Call Logic ---
    const token = localStorage.getItem("token"); // Get token from localStorage
    setLoading(true); // Set loading state to true

    try {
      const payload = {
        category_name: newCategory.category_name.trim(),
        image: newCategory.image.trim(), // Ensure it's trimmed
        description: newCategory.description.trim(),
      };

      const response = await postWithTokenAPI(
        token,
        "/category/create",
        payload
      );

      // --- Response Handling ---
      if (
        response.statusCode === 406 || // Specific status code for already exists
        (response.message && response.message.includes("exists")) // Check message content for "exists"
      ) {
        setAddCategoryErrorMsg("Category already exists.");
        toast.error("Category already exists.");
      } else if (response.statusCode === 200 || response.statusCode === 201) {
        // Success case
        toast.success(response.message || "Category created successfully.");
        setShowAddModal(false); // Close the add category modal
        handleTabChange(activeTab); // Refresh or update categories list if needed

        // Reset the form fields to initial empty state
        setNewCategory({
          category_name: "",
          image: "",
          description: "",
          status: "Active", // Reset to default active
          Approval_status: "Pending", // Reset to default pending
        });
      } else {
        // Generic error response
        setAddCategoryErrorMsg(
          response.message || "Something went wrong. Try again."
        );
        toast.error(response.message || "Something went wrong. Try again.");
      }
    } catch (error) {
      // Network or unexpected error
      console.error("Add category error:", error);
      setAddCategoryErrorMsg(
        "Error adding category. Please check your network connection or server."
      );
      toast.error("Error adding category. Please try again.");
    } finally {
      setLoading(false); // Always set loading state to false after attempt
    }
  };

  return (
    <div className="container-fluid" style={{ position: 'relative' }}>
      {/* Backdrop that appears when modal is open */}
      {(showEditModal || showAddModal) && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1040, // below modal (1050) but above everything else
            opacity: (showEditModal || showAddModal) ? 1 : 0,
            transition: 'opacity 0.15s linear',
          }}
        />
      )}

      <ToastContainer position="top-center" autoClose={1500} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <CategoryIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{ color: primaryGreen, fontWeight: "bold" }}
          >
            Category
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Manage Categories
          </Typography>
        </Box>
      </Box>

      {/* Tab Navigation */}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div
          style={{
            backgroundColor: lightGreen,
            padding: "4px", // Matches p={0.5} from seller tabs
            borderRadius: "8px", // Matches borderRadius={2} from seller tabs
            width: "fit-content",
            display: "inline-flex", // Ensures flex behavior for wrapping
            flexWrap: "wrap", // Allows buttons to wrap
            justifyContent: "flex-start", // Aligns content to the start
          }}
        >
          <ul
            className="nav nav-tabs"
            style={{
              borderBottom: "none",
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
            }}
          >
            <li className="nav-item">
              <button
                className="nav-link"
                style={{
                  // Corrected color logic for active tab: green background, white text
                  backgroundColor: activeTab === "active" ? "#DC3545" : "white",
                  color: activeTab === "active" ? "white" : "#DC3545",
                  border: `1px solid ${
                    activeTab === "active" ? "#DC3545" : "white"
                  }`, // Border matches background
                  marginRight: "4px", // Matches mr={0.5} from seller tabs
                  marginBottom: "2px", // Matches mb={1} from seller tabs
                  fontWeight: "bold",
                  borderRadius: "4px",
                  minWidth: "120px", // Consistent min-width
                  textTransform: "none", // Consistent text casing
                }}
                onClick={() => handleTabChange("active")}
              >
                Active
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link"
                style={{
                  // Corrected color logic for inactive tab: white background, green text
                  backgroundColor:
                    activeTab === "inactive" ? "#DC3545" : "white",
                  color: activeTab === "inactive" ? "white" : "#DC3545",
                  border: `1px solid ${
                    activeTab === "inactive" ? "#DC3545" : "white"
                  }`, // Border matches background
                  marginRight: "4px", // Matches mr={0.5} from seller tabs
                  marginBottom: "2px", // Matches mb={1} from seller tabs
                  fontWeight: "bold",
                  borderRadius: "4px",
                  minWidth: "120px",
                  textTransform: "none",
                }}
                onClick={() => handleTabChange("inactive")}
              >
                Inactive
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link"
                style={{
                  // Corrected color logic
                  backgroundColor:
                    activeTab === "Pending" ? "#DC3545" : "white",
                  color: activeTab === "Pending" ? "white" : "#DC3545",
                  border: `1px solid ${
                    activeTab === "Pending" ? "#DC3545" : "white"
                  }`,
                  marginRight: "4px", // Matches mr={0.5} from seller tabs
                  marginBottom: "2px", // Matches mb={1} from seller tabs
                  fontWeight: "bold",
                  borderRadius: "4px",
                  minWidth: "120px",
                  textTransform: "none",
                }}
                onClick={() => handleTabChange("Pending")}
              >
                Approval Pending
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link"
                style={{
                  // Corrected color logic
                  backgroundColor:
                    activeTab === "Approval" ? "#DC3545" : "white",
                  color: activeTab === "Approval" ? "white" : "#DC3545",
                  border: `1px solid ${
                    activeTab === "Approval" ? "#DC3545" : "white"
                  }`,
                  marginRight: "4px", // Matches mr={0.5} from seller tabs
                  marginBottom: "2px", // Matches mb={1} from seller tabs
                  fontWeight: "bold",
                  borderRadius: "4px",
                  minWidth: "120px",
                  textTransform: "none",
                }}
                onClick={() => handleTabChange("Approval")}
              >
                Approved
              </button>
            </li>
            <li className="nav-item">
              <button
                className="nav-link"
                style={{
                  // Corrected color logic for last button, no right margin
                  backgroundColor: activeTab === "Reject" ? "#DC3545" : "white",
                  color: activeTab === "Reject" ? "white" : "#DC3545",
                  border: `1px solid ${
                    activeTab === "Reject" ? "#DC3545" : "white"
                  }`,
                  marginRight: "0px", // Removed right margin for the last button
                  marginBottom: "2px", // Matches mb={1} from seller tabs
                  fontWeight: "bold",
                  borderRadius: "4px",
                  minWidth: "120px",
                  textTransform: "none",
                }}
                onClick={() => handleTabChange("Reject")}
              >
                Rejected
              </button>
            </li>
          </ul>
        </div>
        {permissions.includes("create_category") && (
          <Button
            variant="contained"
            sx={{
              backgroundColor: primaryGreen,
              "&:hover": {
                backgroundColor: "white",
                border: "2px solid #DC3545",
                color: "#DC3545",
              },
              color: "white",
              fontWeight: "bold",
              textTransform: "none",
              width: { xs: "100%", md: "auto" }, // Full width on small, auto on large
              mt: { xs: 2, md: 0 }, // Margin top on small screens
            }}
            onClick={() => {
              setShowAddModal(true);
              setAddCategoryErrorMsg(""); // Clear error message when opening add modal
              setNewCategory({
                category_name: "",
                image: "",
                description: "",
                status: "Active",
                Approval_status: "Pending",
              });
            }}
          >
            <i className="bi bi-plus-circle me-2"></i>Add Category
          </Button>
        )}
      </div>

      <div className="row">
        <div className="col-12">
       <div className="table-container border rounded">
    <div className="table-responsive">
        {loading ? (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                }}
            >
                <CircularProgress style={{ color: "#DC3545" }} />
                <Typography sx={{ ml: 2,color: "#DC3545" }}>Loading categories...</Typography>
            </Box>
        ) : (
            <>
                {/* Removed 'text-center' class from the table */}
                <table className="table m-0">
                    <thead className="sticky-header fs-6" style={{ backgroundColor: lightGreen }}>
                        <tr>
                            {/* Set text-align to left for all table headers */}
                            <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>S.No</th>
                            <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>Image</th>
                            <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>Category Name</th>
                            {/* <th>Description</th> */}
                            <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>Status</th>
                            <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>Approval Status</th>
                            {
                                permissions.includes("update_category") && (
                                    <th className="table-header" style={{ color: '#777', textAlign: 'left' }}>Action</th>
                                )
                            }
                        </tr>
                    </thead>
                    <tbody>
                        {categories.length > 0 ? (
                            currentCategories.map((category, index) => (
                                <tr key={category._id} style={{ verticalAlign: 'middle' }}>
                                    {/* Set text-align to left for all table data cells */}
                                    {/* <td style={{ textAlign: 'left' }}>{index + 1}</td> */}
                                    <td style={{ textAlign: 'left' }}>{(currentPage - 1) * categoriesPerPage + index + 1}</td>
                                    <td style={{ textAlign: 'left' }}>
                                        <img
                                            src={
                                                category.image ||
                                                "https://via.placeholder.com/50?text=No+Image" // Fallback placeholder image
                                            }
                                            alt={category.category_name || "No Image"}
                                            style={{
                                                width: "50px",
                                                height: "50px",
                                                objectFit: "cover",
                                                borderRadius: "5px",
                                                border: "1px solid #DC3545",
                                            }}
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src =
                                                    "https://via.placeholder.com/50?text=No+Image";
                                            }}
                                        />
                                    </td>
                                    <td
                                        onClick={() => handleEditClick(category)}
                                        style={{
                                            fontWeight: "bold",
                                            color: primaryGreen,
                                            cursor: "pointer",
                                            "&:hover": { textDecoration: "underline" },
                                            fontSize: { xs: "0.8rem", md: "1rem" },
                                            textAlign: 'left' // Explicitly align left
                                        }}
                                    >
                                        {category.category_name}
                                    </td>
                                    {/* <td>{category.description || "-"}</td> */}
                                    <td style={{ textAlign: 'left' }}>
                                        <div className="form-check form-switch d-flex justify-content-start"> {/* Changed to justify-content-start */}
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={category.status === "Active"}
                                                onChange={() =>
                                                    updateCategoryStatus(
                                                        category._id,
                                                        category.status
                                                    )
                                                }
                                                style={{
                                                    width: "3em",
                                                    height: "1.5em",
                                                    backgroundColor:
                                                        category.status === "Active"
                                                            ? "#DC3545"
                                                            : "transparent",
                                                    border:
                                                        category.status !== "Active"
                                                            ? "2px solid #DC3545"
                                                            : "none",
                                                    boxShadow: "none",
                                                }}
                                            />
                                        </div>
                                    </td>
                                    <td style={{ textAlign: 'left' }}>
                                        <span
                                            className={`badge rounded-pill ${
                                                category.Approval_status === "Approval"
                                                    ? "bg-success"
                                                    : category.Approval_status === "Pending"
                                                        ? "bg-warning text-dark"
                                                        : "bg-danger"
                                            }`}
                                        >
                                            {category.Approval_status || "N/A"}
                                        </span>
                                    </td>
                                    {permissions.includes("update_category") && (
                                        <td style={{ textAlign: 'left' }}>
                                            <i
                                                className="bi bi-pencil-square text-success fs-5 cursor-pointer"
                                                title="Edit"
                                                onClick={() => handleEditClick(category)}
                                            ></i>
                                        </td>
                                    )}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center"> {/* Kept center for "No categories found" message */}
                                    No categories found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {/* Pagination Controls */}
                {/* <Box
                    sx={{
                        display: "flex",
                        justifyContent: "end",
                        mt: 3,
                        pb: 2,
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="green"
                        sx={{
                            "& .MuiPaginationItem-root": {
                                color: primaryGreen,
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                                backgroundColor: primaryGreen,
                                color: "white",
                            },
                        }}
                    />
                </Box> */}

                {/* Pagination Controls - Only show if needed */}
{(activeTab === "Pending" ? categories.length > categoriesPerPage : totalPages > 1) && (
  <Box
    sx={{
      display: "flex",
      justifyContent: "end",
      mt: 3,
      pb: 2,
    }}
  >
    <Pagination
      count={totalPages}
      page={currentPage}
      onChange={handlePageChange}
      color="green"
      sx={{
        "& .MuiPaginationItem-root": {
          color: primaryGreen,
        },
        "& .MuiPaginationItem-root.Mui-selected": {
          backgroundColor: primaryGreen,
          color: "white",
        },
      }}
    />
  </Box>
)}
            </>
        )}
    </div>
</div>
        </div>
      </div>

      {/* Edit Modal for Approval Status (Bootstrap Modal) */}
      {/* {showEditModal && selectedCategory && (
        <div 
          className="modal show d-block" 
          tabIndex="-1" 
          role="dialog"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto'
          }}
        >
          <div className="modal-dialog modal-lg" role="document">
            <div className="modal-content">
              <div
                className="modal-header text-white"
                style={{ backgroundColor: "#DC3545" }}
              >
                <h5 className="modal-title">Update Approval Status</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row mb-3">
                  <div className="col-md-4 d-flex justify-content-center align-items-center">
                    <img
                      src={
                        selectedCategory.image ||
                        "https://via.placeholder.com/150?text=Category"
                      }
                      alt={selectedCategory.category_name}
                      className="img-fluid rounded"
                      style={{
                        maxHeight: "150px",
                        border: "2px solid #DC3545",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/150?text=Category";
                      }}
                    />
                  </div>
                  <div className="col-md-8">
                    <div className="row">
                      <div className="col-12 mb-2">
                        <label className="form-label text-muted small">
                          Category Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedCategory.category_name || ""}
                          onChange={(e) =>
                            setSelectedCategory((prev) => ({
                              ...prev,
                              category_name: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-12 mb-2">
                        <label className="form-label text-muted small">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          value={selectedCategory.description || ""}
                          rows="3"
                          onChange={(e) =>
                            setSelectedCategory((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-12 mb-2">
                        <label className="form-label text-muted small">
                          Status
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedCategory.status || "-"}
                          disabled
                        />
                      </div>
                      <div className="col-12 mb-2">
                        <label className="form-label text-muted small">
                          Current Approval Status
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedCategory.Approval_status || "N/A"}
                          disabled
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label htmlFor="approvalStatusSelect" className="form-label">
                    Approval Status
                  </label>
                  <select
                    className="form-select"
                    id="approvalStatusSelect"
                    value={approvalStatus}
                    onChange={(e) => setApprovalStatus(e.target.value)}
                  >
                    <option value="Pending">Pending</option>
                    <option value="Approval">Approved</option>
                    <option value="Reject">Rejected</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn text-white"
                  style={{ backgroundColor: "#DC3545", fontWeight: "bold" }}
                  onClick={updateApprovalStatus}
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update Status"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {showEditModal && selectedCategory && (
  <div 
    className="modal show d-block" 
    tabIndex="-1" 
    role="dialog"
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1050,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'auto'
    }}
  >
    <div className="modal-dialog modal-xl" role="document">
      <div className="modal-content">
        <div
          className="modal-header text-white"
          style={{ backgroundColor: "#DC3545" }}
        >
          <h5 className="modal-title">Update Category</h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={() => setShowEditModal(false)}
          ></button>
        </div>
        <div className="modal-body">
          <div className="row mb-3">
            <div className="col-md-4 d-flex flex-column justify-content-center align-items-center">
              <img
                src={
                  selectedCategory.image ||
                  "https://via.placeholder.com/150?text=Category"
                }
                alt={selectedCategory.category_name}
                className="img-fluid rounded mb-2"
                style={{
                  maxHeight: "150px",
                  maxWidth: "100%",
                  border: "2px solid #DC3545",
                }}
                onError={(e) => {
                  e.target.src =
                    "https://via.placeholder.com/150?text=Category";
                }}
              />
              <div className="w-100">
                <label className="form-label text-muted small">
                  Image URL
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={selectedCategory.image || ""}
                  onChange={(e) =>
                    setSelectedCategory((prev) => ({
                      ...prev,
                      image: e.target.value,
                    }))
                  }
                  placeholder="Enter image URL"
                />
              </div>
            </div>
            <div className="col-md-8">
              <div className="row">
                <div className="col-12 mb-2">
                  <label className="form-label text-muted small">
                    Category Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedCategory.category_name || ""}
                    onChange={(e) =>
                      setSelectedCategory((prev) => ({
                        ...prev,
                        category_name: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="col-12 mb-2">
                  <label className="form-label text-muted small">
                    Description
                  </label>
                  <textarea
                    className="form-control"
                    value={selectedCategory.description || ""}
                    rows="3"
                    onChange={(e) =>
                      setSelectedCategory((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="col-12 mb-2">
                  <label className="form-label text-muted small">
                    Status
                  </label>
                  <select
                    className="form-select"
                    value={selectedCategory.status || "Active"}
                    onChange={(e) =>
                      setSelectedCategory((prev) => ({
                        ...prev,
                        status: e.target.value,
                      }))
                    }
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-12 mb-2">
                  <label className="form-label text-muted small">
                    Current Approval Status
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={selectedCategory.Approval_status || "N/A"}
                    disabled
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="approvalStatusSelect" className="form-label">
              Update Approval Status
            </label>
            <select
              className="form-select"
              id="approvalStatusSelect"
              value={approvalStatus}
              onChange={(e) => setApprovalStatus(e.target.value)}
            >
              <option value="Pending">Pending</option>
              <option value="Approval">Approved</option>
              <option value="Reject">Rejected</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowEditModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn text-white"
            style={{ backgroundColor: "#DC3545", fontWeight: "bold" }}
            onClick={updateApprovalStatus}
            disabled={loading}
          >
            {loading ? "Updating..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Add Category Modal (Bootstrap Modal) */}
      {showAddModal && (
        <div 
          className="modal show d-block" 
          tabIndex="-1" 
          role="dialog"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'auto'
          }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div
                className="modal-header text-white"
                style={{ backgroundColor: "#DC3545" }}
              >
                <h5 className="modal-title">Add New Category</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowAddModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {addCategoryErrorMsg && (
                  <p className="text-danger">{addCategoryErrorMsg}</p>
                )}
                <form onSubmit={handleAddCategory}>
                  <div className="mb-3">
                    <label htmlFor="categoryName" className="form-label">
                      Category Name<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        addCategoryErrorMsg &&
                        newCategory.category_name.trim() === ""
                          ? "is-invalid"
                          : ""
                      }`}
                      id="categoryName"
                      value={newCategory.category_name}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          category_name: e.target.value,
                        })
                      }
                      required // HTML5 validation for empty field
                    />
                    {addCategoryErrorMsg &&
                      newCategory.category_name.trim() === "" && (
                        <div className="invalid-feedback">
                          Category name is required.
                        </div>
                      )}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="imageUrl" className="form-label">
                      Image URL<span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${
                        addCategoryErrorMsg &&
                        (newCategory.image.trim() === "" ||
                          (newCategory.image.trim() !== "" &&
                            !newCategory.image
                              .trim()
                              .match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i)))
                          ? "is-invalid"
                          : ""
                      }`}
                      id="imageUrl"
                      value={newCategory.image}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          image: e.target.value,
                        })
                      }
                      required // HTML5 validation for empty field
                    />
                    {addCategoryErrorMsg && newCategory.image.trim() === "" && (
                      <div className="invalid-feedback">
                        Image URL is required.
                      </div>
                    )}
                    {addCategoryErrorMsg &&
                      newCategory.image.trim() !== "" &&
                      !newCategory.image
                        .trim()
                        .match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i) && (
                        <div className="invalid-feedback">
                          Please enter a valid Image URL.
                        </div>
                      )}
                  </div>
                  {newCategory.image &&
                    newCategory.image.trim() !== "" &&
                    newCategory.image
                      .trim()
                      .match(/^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/i) && (
                      <div className="mb-3 text-center">
                        <p className="text-muted small mb-1">Image Preview:</p>
                        <img
                          src={newCategory.image}
                          alt="Category Preview"
                          style={{
                            width: "100px",
                            height: "100px",
                            objectFit: "cover",
                            borderRadius: "5px",
                            border: "1px solid #DC3545",
                          }}
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/100?text=Invalid+URL";
                          }}
                        />
                      </div>
                    )}
                  <div className="mb-3">
                    <label htmlFor="description" className="form-label">
                      Description<span className="text-danger">*</span>
                    </label>
                    <textarea
                      className={`form-control ${
                        addCategoryErrorMsg &&
                        newCategory.description.trim() === ""
                          ? "is-invalid"
                          : ""
                      }`}
                      id="description"
                      rows="3"
                      value={newCategory.description}
                      onChange={(e) =>
                        setNewCategory({
                          ...newCategory,
                          description: e.target.value,
                        })
                      }
                      required // HTML5 validation for empty field
                    ></textarea>
                    {addCategoryErrorMsg &&
                      newCategory.description.trim() === "" && (
                        <div className="invalid-feedback">
                          Description is required.
                        </div>
                      )}
                  </div>
                  <div className="modal-footer justify-content-end pb-0 pe-0">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowAddModal(false)}
                      disabled={loading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn text-white"
                      style={{ backgroundColor: "#DC3545", fontWeight: "bold" }}
                      disabled={loading}
                    >
                      {loading ? "Adding..." : "Add Category"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManagement;
